import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { AUTH_TOKEN_KEY, resolveLoginReturnPath } from '../../utils/authUtils';
import { resolveAuthCallbackLanding } from '../../utils/portalLandingRoute';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { processOAuthSession, checkAuth, user } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const token = params.get('token');
        const next = resolveLoginReturnPath(params.get('next'), window.location.origin);
        const hostname = window.location.hostname;

        if (token) {
          localStorage.setItem(AUTH_TOKEN_KEY, token);
          const authenticatedUser = await checkAuth();
          toast.success('Welcome to FundElite AI!');
          const landing = resolveAuthCallbackLanding({
            next,
            hostname,
            user: authenticatedUser || user,
          });
          navigate(landing, { replace: true });
          return;
        }

        const hashParams = new URLSearchParams(location.hash.replace('#', ''));
        const sessionId = hashParams.get('session_id');

        if (!sessionId) {
          toast.error('Authentication failed: No session information received');
          navigate('/login');
          return;
        }

        const sessionUser = await processOAuthSession(sessionId);
        toast.success('Welcome to FundElite AI!');
        navigate(
          resolveAuthCallbackLanding({
            next,
            hostname,
            user: sessionUser || user,
          }),
          { replace: true }
        );
      } catch (error) {
        console.error('OAuth callback error:', error);
        toast.error('Authentication failed. Please try again.');
        navigate('/login');
      }
    };

    processAuth();
  }, [location, navigate, processOAuthSession, checkAuth, user]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-stone-900">Completing sign in...</h2>
        <p className="text-slate-500 mt-2">Please wait while we set up your account</p>
      </div>
    </div>
  );
};

export default AuthCallback;
